import React from "react";
import PropTypes from 'prop-types';

import IconContext from '../context/IconContext';

// Define the type of the React component being given via context
type SvgComponentProps = React.SVGProps<SVGSVGElement> 
// Create an interface containing the icons needed for this component
interface IIconContext {
    IconCancelWhite: React.ComponentType<SvgComponentProps>;

    // To use custom SVGs, update the config to define these keys.
    IconChipClose: React.ComponentType<SvgComponentProps>;
}

interface ChipProps extends React.HTMLAttributes<HTMLElement> {
    isInactive?: boolean;
    chipText?: string;
    hideClose?: boolean;
    // Baseline arguments
    children?: React.ReactNode;
    className?: string;
    // Functions
    onClose?: () => void
}

/**
 * A small chip that displays some text. Can be placed in a `ChipGroup`, or kept standalone.
 * 
 * ## _
 * ### Arguments
 * @param {string} chipText - The text to be displayed on the chip.
 * @param {boolean} isInactive - An optional field that will make the chip inactive.
 * @param {boolean} hideClose - An optional field that will hide the close button.
 * @param {React.ReactNode} children - The contents inside the component. This is not used in chips, but is kept in for parity.
 * @param {string} className - CSS classes to apply to the component.
 * 
 * ## _
 * ### Functions
 * @param {function} onClose - The function triggered when the chip closes.
 */
class Chip extends React.Component<ChipProps> {
    // Establish the context and tell TypeScript that we're TOTALLY having it in the shape of this thing.
    static contextType = IconContext;
    context!: IIconContext;

    static propTypes = {
        chipText: PropTypes.string,
        isInactive: PropTypes.bool,
        hideClose: PropTypes.bool,
        children: PropTypes.node,
        className: PropTypes.string,
        onClose: PropTypes.func,
    }

    static defaultProps = {
        className: "",
    }

    render() {
        const { chipText, isInactive, hideClose, children, className, onClose, ...rest } = this.props;
        const {
            IconCancelWhite,
            IconChipClose,
        } = this.context;

        const CloseIcon = IconChipClose || IconCancelWhite;

        let classes = `a-chip`;

        if (isInactive) classes += ` a-chip--inactive`;

        if (className) classes += ` ${className}`

        return (
            <div className={classes} {...rest}>
                <p className="a-chip__label">
                    <span className="a-chip__category">{chipText}</span>
                </p>
                {!hideClose &&
                    <button className="a-chip__remove-btn" onClick={onClose}>
                        <CloseIcon className="a-chip__icon" />
                    </button>
                }
            </div>
        );
    }
}

export default Chip;